import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ListChecks, Loader2 } from 'lucide-react'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { proposeNoteTasks, type NoteTaskProject } from 'services/aiService'
import { aiPlanReceived } from 'slices/aiSlice'

import { PlanCard } from './PlanCard'

type Phase = { kind: 'loading' } | { kind: 'choose'; projects: NoteTaskProject[] } | { kind: 'plan'; planId: string } | { kind: 'error'; message: string }

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't read tasks from this note just now. Try again in a moment."

/**
 * "Make tasks" from a location note (MYD-643).
 *
 * Asks the server for a plan of tasks read out of the note, then shows it on
 * the ordinary plan card, where the user edits, picks, confirms and can undo.
 * Nothing is created until they confirm there.
 *
 * A note on a location with several projects comes back 409 CHOOSE_PROJECT
 * with the list; the user picks once here, rather than on every step.
 */
export function NoteTasksDialog({ open, onClose, noteId, propose = proposeNoteTasks }: { open: boolean; onClose: () => void; noteId: string; propose?: typeof proposeNoteTasks }) {
	const dispatch = useDispatch()
	const [phase, setPhase] = useState<Phase>({ kind: 'loading' })

	const ask = useCallback(
		async (projectId?: string) => {
			setPhase({ kind: 'loading' })
			try {
				const plan = await propose(noteId, projectId)
				dispatch(aiPlanReceived(plan))
				setPhase({ kind: 'plan', planId: plan.id })
			} catch (error: any) {
				const data = error?.response?.data
				if (error?.response?.status === 409 && data?.code === 'CHOOSE_PROJECT' && Array.isArray(data.projects)) setPhase({ kind: 'choose', projects: data.projects })
				else setPhase({ kind: 'error', message: messageOf(error) })
			}
		},
		[dispatch, noteId, propose],
	)

	useEffect(() => {
		if (open) ask()
	}, [open, ask])

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-h-[90vh] w-full max-w-xl overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<ListChecks className="h-5 w-5 text-primary" aria-hidden="true" />
						Tasks from this note
					</DialogTitle>
					<DialogDescription>Ask MDT reads the note and proposes tasks. Nothing is created until you confirm.</DialogDescription>
				</DialogHeader>

				{phase.kind === 'loading' && (
					<p className="flex items-center gap-2 text-sm text-gray-600" role="status">
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Reading the note…
					</p>
				)}

				{phase.kind === 'choose' && (
					<div className="space-y-2">
						<p className="text-sm text-gray-700">Which project are these tasks for?</p>
						<ul className="space-y-1.5">
							{phase.projects.map(project => (
								<li key={project.id}>
									<button
										type="button"
										onClick={() => ask(project.id)}
										className="w-full rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-900 hover:border-primary hover:bg-primary/5"
									>
										{project.name}
									</button>
								</li>
							))}
						</ul>
					</div>
				)}

				{phase.kind === 'error' && (
					<p role="alert" className="text-sm text-destructive">
						{phase.message}
					</p>
				)}

				{phase.kind === 'plan' && <PlanCard planId={phase.planId} />}
			</DialogContent>
		</Dialog>
	)
}

export default NoteTasksDialog
